import React, { useState, useCallback } from "react";
import { Phone, ArrowRight, X, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Formulaire express mobile sticky en bas de l'écran.
 * Visible uniquement sur mobile (< lg breakpoint).
 * Champs : Prénom + Téléphone uniquement pour conversion max.
 */
export default function MobileLeadForm() {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ first_name: "", phone: "" });

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.phone) return;
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/api/loomy/leads`, {
        first_name: form.first_name,
        phone: form.phone,
        source: "Formulaire Express Mobile",
        tags: ["express", "mobile"],
      });
      setSubmitted(true);
      toast.success("Merci ! Nous vous rappelons très vite.");
      // Track conversion
      if (typeof window.fbq === "function") window.fbq("track", "Lead", { content_name: "express_mobile" });
      if (typeof window.gtag === "function") window.gtag("event", "Lead", { content_name: "express_mobile" });
    } catch {
      toast.error("Erreur, veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  }, [form]);

  // Don't show if already submitted
  if (submitted) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 lg:hidden" data-testid="mobile-lead-form">
      {/* Collapsed bar */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          data-testid="mobile-lead-trigger"
          className="w-full bg-accent text-white py-3.5 px-4 flex items-center justify-center gap-2 font-bold text-sm shadow-lg shadow-accent/30"
        >
          <Phone className="w-4 h-4" />
          Être rappelé gratuitement
          <ArrowRight className="w-4 h-4" />
        </button>
      )}

      {/* Expanded form */}
      {isOpen && (
        <div className="bg-white border-t-2 border-accent shadow-2xl px-4 pt-4 pb-5 safe-area-bottom">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-bold text-slate-900">Rappel gratuit en quelques minutes</p>
            <button
              onClick={() => setIsOpen(false)}
              data-testid="mobile-lead-close"
              className="p-1.5 rounded-md hover:bg-slate-100 transition-colors"
            >
              <X className="w-4 h-4 text-slate-400" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="flex flex-col gap-2.5">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={form.first_name}
                  onChange={(e) => setForm((p) => ({ ...p, first_name: e.target.value }))}
                  placeholder="Prénom"
                  data-testid="mobile-lead-name"
                  className="w-full pl-9 pr-3 py-3 border-2 border-slate-200 rounded-md text-sm focus:border-primary focus:outline-none"
                  required
                />
              </div>
              <div className="relative flex-1">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                  placeholder="+41 79..."
                  data-testid="mobile-lead-phone"
                  className="w-full pl-9 pr-3 py-3 border-2 border-slate-200 rounded-md text-sm focus:border-primary focus:outline-none"
                  required
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={isSubmitting || !form.first_name || !form.phone}
              data-testid="mobile-lead-submit"
              className="w-full bg-accent hover:bg-orange-600 text-white font-bold py-3 rounded-md transition-all"
            >
              {isSubmitting ? "Envoi..." : "Me rappeler maintenant"}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <p className="text-[0.6rem] text-slate-400 text-center">
              En soumettant, vous acceptez d'être contacté par Lead Artisan.
            </p>
          </form>
        </div>
      )}
    </div>
  );
}
